const LIVE_CHAT_SCRIPT_ID = 'live-chat-widget-script';
const LIVE_CHAT_SCRIPT_URL = 'https://cdn.livechatinc.com/tracking.js';

type LiveChatListener = (...args: unknown[]) => void;

interface LiveChatWidgetStub {
  _q?: unknown[];
  _h?: null | ((...args: unknown[]) => unknown);
  _v?: string;
  on: (event: string, listener: LiveChatListener) => void;
  once: (event: string, listener: LiveChatListener) => void;
  off: (event: string, listener: LiveChatListener) => void;
  get?: (...args: unknown[]) => unknown;
  call: (method: string, ...args: unknown[]) => void;
  init?: () => void;
}

interface LiveChatWidgetSnippet extends LiveChatWidgetStub {
  _q: unknown[];
  _h: null | ((...args: unknown[]) => unknown);
  _v: string;
  get: (...args: unknown[]) => unknown;
  init: () => void;
}

declare global {
  interface Window {
    __lc?: {
      asyncInit?: boolean;
      integration_name?: string;
      license?: number;
      product_name?: string;
      visitor?: {
        email?: string;
        name?: string;
      };
    };
    LiveChatWidget?: LiveChatWidgetStub;
  }
}

let initPromise: Promise<void> | null = null;
let readyPromise: Promise<void> | null = null;

function createLiveChatStub() {
  window.__lc = window.__lc ?? {};
  window.__lc.license = 14785209;
  window.__lc.integration_name = 'manual_channels';
  window.__lc.product_name = 'livechat';

  (function (n: Window, t: Document) {
    function toArray(args: IArguments) {
      return Array.prototype.slice.call(args);
    }

    function i(args: unknown[]) {
      return e._h ? e._h.apply(null, args) : e._q.push(args);
    }

    const e: LiveChatWidgetSnippet = {
      _q: [],
      _h: null,
      _v: '2.0',
      on: function () {
        i(['on', toArray(arguments)]);
      },
      once: function () {
        i(['once', toArray(arguments)]);
      },
      off: function () {
        i(['off', toArray(arguments)]);
      },
      get: function () {
        if (!e._h) {
          throw new Error(
            "[LiveChatWidget] You can't use getters before load."
          );
        }

        return i(['get', toArray(arguments)]);
      },
      call: function () {
        i(['call', toArray(arguments)]);
      },
      init: function () {
        const existingScript = t.getElementById(LIVE_CHAT_SCRIPT_ID);

        if (existingScript) return;

        const script = t.createElement('script');
        script.async = true;
        script.type = 'text/javascript';
        script.id = LIVE_CHAT_SCRIPT_ID;
        script.src = LIVE_CHAT_SCRIPT_URL;
        t.head.appendChild(script);
      },
    };

    if (!n.__lc?.asyncInit) {
      e.init();
    }

    n.LiveChatWidget = n.LiveChatWidget ?? e;
  })(window, document);
}

async function waitForLiveChatReady() {
  if (window.LiveChatWidget?._h) {
    return;
  }

  if (!readyPromise) {
    readyPromise = new Promise((resolve) => {
      window.LiveChatWidget?.on('ready', () => {
        resolve();
      });
    });
  }

  return readyPromise;
}

export async function initLiveChat() {
  if (typeof window === 'undefined') {
    return;
  }

  if (initPromise) {
    return initPromise;
  }

  initPromise = new Promise((resolve) => {
    createLiveChatStub();
    resolve();
  });

  return initPromise;
}

export async function openLiveChat() {
  await initLiveChat();
  await waitForLiveChatReady();

  window.LiveChatWidget?.call('maximize');
}

export function hideLiveChat() {
  if (!window.LiveChatWidget) return;

  window.LiveChatWidget.call('hide');
}
